import React from 'react'
import { Box, Typography, Button, CircularProgress } from '@mui/material'
import { Delete } from '@mui/icons-material'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import theme from '../theme'
import CreativeClockIcon from '../icons/CreativeClockIcon'
import ContentCategory from '../icons/ContentCategory'
import { formatDate, getCreativeStatus } from '../utils'
import StatusChip from '../StatusChip'
import ContentCategoryMismatchBadge from '../guidelines/ContentCategoryMismatchBadge'
import { CreativeUploadResponse, Flight, CampaignStatus, ContentCategory as ContentCategoryType } from '../../types/campaign'

dayjs.extend(timezone)

const THUMBNAIL_TYPE = 'thumbnail'

interface Props {
  creative: CreativeUploadResponse
  flight: Flight
  campaignStatus: CampaignStatus
  onDelete: () => void
  onEdit: () => void
  campaignContentCategory?: ContentCategoryType | null
  allContentCategories?: ContentCategoryType[]
  canDelete?: boolean
}

const CreativeDetails = ({ creative, flight, campaignStatus, onDelete, onEdit, campaignContentCategory, allContentCategories, canDelete = true }: Props) => {
  const creativeContentCategoryObject = allContentCategories?.find(c => c.name === creative.content_category) || null
  const pacificTimeZone = 'America/Los_Angeles'
  return (
    <Box
      key={creative.id}
      sx={{
        display: 'flex',
        flexDirection: {
          xs: 'column',
          md: 'row'
        },
        gap: theme.space['8'],
        justifyContent: 'space-between',
        padding: '24px 0',
        borderBottom: `1px solid ${theme.palette['sage-100']}`
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            md: 'row'
          },
          gap: theme.space['8'],
        }}
      >
        <Box
          sx={{
            width: 100,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: theme.space['32'],
            backgroundColor: theme.palette['sage-50'],
            borderRadius: theme.borders['rounded-10']
          }}
        >
          {creative.review_status && creative.review_status.toLowerCase() === 'transcoding' ? (
            <CircularProgress size={24} sx={{ color: theme.palette['accent-500'] }} />
          ) : (
            <img
              width={100}
              src={
                creative.assets.find((asset) => asset.type === THUMBNAIL_TYPE)?.url
              }
            />
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space['12']
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                md: 'row'
              },
              gap: theme.space['12'],
            }}>
            <Typography
              variant="body-11"
              sx={{
                fontWeight: 600,
                maxWidth: {
                  xs: '200px',
                  sm: '300px',
                  md: '400px',
                  lg: '500px',
                  xl: '600px'
                }
              }}
              noWrap
            >
              {creative.name}
            </Typography>
            <StatusChip
              status={getCreativeStatus(creative, flight, campaignStatus)}
              onStatusChange={() => null}
              isUpdating
            />
          </Box>
          <Typography
            variant="body-11"
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row'
              },
              alignItems: {
                xs: 'flex-start',
                sm: 'center'
              },
              gap: theme.space['12']
            }}
          >
            <CreativeClockIcon
              assetDuration={creative.asset_duration_ms / 1000}
            />
            {creative.content_category && (
              <ContentCategory contentCategoryValue={creative.content_category} />
            )}
            <Box sx={{ display: 'flex', gap: theme.space['8'] }}>
              <Typography variant="body-11" color={theme.palette['sage-400']}>
                Start
              </Typography>
              {`${formatDate(
                creative.calculated_duration.starts_at,
                pacificTimeZone
              )}`}
            </Box>
            <Box sx={{ display: 'flex', gap: theme.space['8'] }}>
              <Typography variant="body-11" color={theme.palette['sage-400']}>
                End
              </Typography>
              {`${formatDate(
                creative.calculated_duration.ends_at,
                pacificTimeZone
              )}`}
            </Box>
            <ContentCategoryMismatchBadge
              creativeContentCategory={creativeContentCategoryObject}
              campaignContentCategory={campaignContentCategory ?? null}
              allContentCategories={allContentCategories}
            />
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: theme.space['12'],
          alignItems: 'center',
          height: '32px'
        }}
      >
        <Button
          color="secondary"
          variant="contained"
          title="Edit"
          onClick={onEdit}
        >
          <Typography variant="ui-1" color={theme.palette['sage-900']}>
            Edit
          </Typography>
        </Button>
        <Button
          variant="contained"
          size="small"
          color="secondary"
          title="Delete"
          disabled={!canDelete}
          onClick={onDelete}
        >
          <Delete
            fontSize="small"
            sx={{
              color: canDelete
                ? theme.palette.text.primary
                : theme.palette['sage-300']
            }}
          />
        </Button>
      </Box>
    </Box>
  )
}

export default CreativeDetails
